import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Heart, 
  User, 
  Book, 
  Shield, 
  Menu, 
  X, 
  Home as HouseIcon,
  Building,
  Code,
  Calendar,
  Truck,
  FileText,
  Crown,
  LogOut,
  Key
} from 'lucide-react';
import { supabase } from '../lib/supabase';

export function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [userRole, setUserRole] = useState<'admin' | 'landlord' | 'user' | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      
      if (session?.user) {
        setIsAuthenticated(true);
        
        // Get user role from metadata
        const role = session.user.user_metadata?.role || 'user';
        setUserRole(role);
        
        // Get user profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', session.user.id)
          .single();
        
        setUserName(profile?.display_name || session.user.email || 'User');
      } else {
        setIsAuthenticated(false);
        setUserRole(null);
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      setIsAuthenticated(false);
      setUserRole(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth');
      setIsOpen(false);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  // Define navigation items based on user role
  const getNavigationItems = () => {
    const commonItems = [
      { name: 'Home', path: '/', icon: Home, public: true },
      { name: 'Sign Up / Login', path: '/auth', icon: Key, public: true, hideWhenAuth: true },
    ];

    const userItems = [
      { name: 'All Listings', path: '/listings', icon: Building, auth: true },
      { name: 'Roommate Matches', path: '/matches', icon: Users, auth: true },
      { name: 'LoneStay', path: '/lonestay', icon: HouseIcon, auth: true },
      { name: 'Profile', path: '/profile', icon: User, auth: true },
      { name: 'Feedback', path: '/guide', icon: Book, auth: true },
    ];

    const adminItems = [
      { name: 'Admin Dashboard', path: '/admin', icon: Shield, admin: true },
      { name: 'Users', path: '/users', icon: Users, admin: true },
      { name: 'Monetization', path: '/monetization', icon: Crown, admin: true },
      { name: 'Events', path: '/events', icon: Calendar, admin: true },
      { name: 'Furniture Pickup', path: '/pickup', icon: Truck, admin: true },
      { name: 'Contracts', path: '/contracts', icon: FileText, admin: true },
      { name: 'Developer', path: '/dev', icon: Code, admin: true },
    ];

    const landlordItems = [
      { name: 'Landlord Dashboard', path: '/landlord', icon: Building, landlord: true },
      { name: 'My Properties', path: '/properties', icon: HouseIcon, landlord: true },
      { name: 'Tenant Applications', path: '/applications', icon: FileText, landlord: true },
    ];

    let items = [...commonItems];

    if (userRole === 'admin') {
      items = [...items, ...adminItems];
    } else if (userRole === 'landlord') {
      items = [...items, ...landlordItems];
    } else if (isAuthenticated) {
      items = [...items, ...userItems];
    }

    return items.filter(page => {
      if (page.admin && userRole !== 'admin') return false;
      if (page.landlord && userRole !== 'landlord') return false;
      if (page.auth && !isAuthenticated) return false;
      if (page.hideWhenAuth && isAuthenticated) return false;
      if (page.public) return true;
      return isAuthenticated;
    });
  };

  const pages = getNavigationItems();

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  if (isLoading) {
    return null; // Don't render navigation while loading
  }

  return (
    <>
      {/* Role Badge */}
      {isAuthenticated && userRole && (
        <div className={`fixed top-4 right-20 z-50 px-3 py-1 rounded-full text-sm font-medium shadow-sm ${
          userRole === 'admin' ? 'bg-primary-600 text-white' :
          userRole === 'landlord' ? 'bg-earth-400 text-white' :
          'bg-cream-400 text-earth-800'
        }`}>
          {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
        </div>
      )}

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 right-4 z-50 md:hidden bg-white p-2 rounded-lg shadow-sm"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-primary-600" />
        ) : (
          <Menu className="w-6 h-6 text-primary-600" />
        )}
      </button>

      {/* Desktop Sidebar */}
      <div className="hidden md:flex fixed left-0 top-0 h-full bg-white shadow-lg w-64 p-6 z-40">
        <nav className="w-full flex flex-col justify-between h-full">
          <div>
            <div className="mb-8">
              <div className="flex items-center gap-3">
                {userRole === 'admin' ? (
                  <Shield className="w-8 h-8 text-primary-400" />
                ) : userRole === 'landlord' ? (
                  <Building className="w-8 h-8 text-earth-400" />
                ) : (
                  <Heart className="w-8 h-8 text-primary-400" />
                )}
                <h1 className="text-2xl font-serif text-primary-600">
                  {userRole === 'admin' ? 'Admin Panel' : 
                   userRole === 'landlord' ? 'Landlord Panel' : 
                   'Find Your People'}
                </h1>
              </div>
              {isAuthenticated && (
                <p className="text-sm text-gray-600 mt-2 ml-1">
                  Welcome, {userName}
                </p>
              )}
            </div>
            <div className="space-y-2">
              {pages.map((page) => {
                const Icon = page.icon;
                const isActive = location.pathname === page.path;
                return (
                  <button
                    key={page.path}
                    onClick={() => handleNavigation(page.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary-50 text-primary-600'
                        : 'hover:bg-gray-50 text-gray-600 hover:text-primary-600'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{page.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {isAuthenticated && (
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors mt-auto"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          )}
        </nav>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40">
          <div ref={mobileMenuRef} className="bg-white w-64 h-full p-6 flex flex-col">
            <div className="mb-8">
              <div className="flex items-center gap-3">
                {userRole === 'admin' ? (
                  <Shield className="w-8 h-8 text-primary-400" />
                ) : userRole === 'landlord' ? (
                  <Building className="w-8 h-8 text-earth-400" />
                ) : (
                  <Heart className="w-8 h-8 text-primary-400" />
                )}
                <h1 className="text-2xl font-serif text-primary-600">
                  {userRole === 'admin' ? 'Admin Panel' : 
                   userRole === 'landlord' ? 'Landlord Panel' : 
                   'Find Your People'}
                </h1>
              </div>
              {isAuthenticated && (
                <p className="text-sm text-gray-600 mt-2 ml-1">
                  Welcome, {userName}
                </p>
              )}
            </div>
            <nav className="space-y-2 flex-1">
              {pages.map((page) => {
                const Icon = page.icon;
                const isActive = location.pathname === page.path;
                return (
                  <button
                    key={page.path}
                    onClick={() => handleNavigation(page.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary-50 text-primary-600'
                        : 'hover:bg-gray-50 text-gray-600 hover:text-primary-600'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{page.name}</span>
                  </button>
                );
              })}
            </nav>
            
            {isAuthenticated && (
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors mt-4"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}